import ApiBase from './api-base'
import * as types from '@/store/types'

export default class Account extends ApiBase {
  constructor (store) {
    super(store)
    this._userType = this._config.auth.userType
  }

  getUserDisplayName (user) {
    let result = ''

    if (user.companyName) result += user.companyName
    else if (user.name) result += user.name
    else if (user.firstName || user.lastName) {
      if (user.firstName) result += `${user.firstName} `
      if (user.lastName) result += ` ${user.lastName}`
    } else if (user.phoneNumber) result += user.phoneNumber
    else if (user.email) result += user.email
    else if (user.userName) result += user.userName
    else result += user.id

    return result.trim()
  }

  // Usertype Endpoints -----------------------

  getProfile (filter, showProgress) {
    return this._request({
      action: `${this._userType}/profile`,
      method: 'GET',
      params: filter,
      showProgress
    })
  }

  updateProfile (model, showProgress) {
    return this._request({
      action: `${this._userType}/profile`,
      method: 'PUT',
      body: model,
      showProgress
    })
  }

  getCourierData (showProgress) {
    return this._request({
      action: `${this._userType}/profile/courier-data`,
      method: 'GET',
      showProgress
    })
  }

  updateCourierData (model, showProgress) {
    return this._request({
      action: `${this._userType}/profile/courier-data`,
      method: 'PUT',
      body: model,
      showProgress
    })
  }

  getDocuments (showProgress) {
    return this._request({
      action: `${this._userType}/profile/documents`,
      method: 'GET',
      showProgress
    })
  }

  updateDocuments (model, showProgress) {
    return this._request({
      action: `${this._userType}/profile/documents`,
      method: 'PUT',
      body: model,
      showProgress
    })
  }

  getSignatories (showProgress) {
    return this._request({
      action: `${this._userType}/profile/signatories`,
      method: 'GET',
      showProgress
    })
  }

  updateSignatories (model, showProgress) {
    return this._request({
      action: `${this._userType}/profile/signatories`,
      method: 'PUT',
      body: model,
      showProgress
    })
  }

  getBankAccounts (showProgress) {
    return this._request({
      action: `${this._userType}/profile/bank-accounts`,
      method: 'GET',
      showProgress
    })
  }

  updateBankAccounts (model, showProgress) {
    return this._request({
      action: `${this._userType}/profile/bank-accounts`,
      method: 'PUT',
      body: model,
      showProgress
    })
  }

  getContacts (showProgress) {
    return this._request({
      action: `${this._userType}/profile/contacts`,
      method: 'GET',
      showProgress
    })
  }

  updateContacts (model, showProgress) {
    return this._request({
      action: `${this._userType}/profile/contacts`,
      method: 'PUT',
      body: model,
      showProgress
    })
  }

  courierDataApprovementRequest (showProgress) {
    return this._request({
      action: `${this._userType}/profile/courier-data-approvement-request`,
      method: 'POST',
      showProgress
    })
  }

  refreshStatus () {
    return this._request({
      action: `${this._userType}/profile/status`,
      method: 'GET'
    })
    .then(res => {
      this._store.commit(types.SET_USER_STATUS, res)
    })
  }

  isInAnyOfRoles(roles) {
    const account = this._store.state.$account
    return account && !!account.roles.find(accountRole => roles.includes(accountRole))
  }

  isInRole(role) {
    return this.isInAnyOfRoles([role])
  }

  hasEveryStatuses(statuses) {
    const userStatus = this._store.state.$userStatus
    return userStatus && statuses.every(s => userStatus[s])
  }

  hasStatus(status) {
    return this.hasEveryStatuses([status])
  }

  checkRolesAndStatuses (item) {
    const roles = item.roles || []
    const statuses = item.statuses || []

    if (!roles.length && !statuses.length) return true

    if (roles && roles.length && !this.isInAnyOfRoles(roles)) return false

    if (statuses && statuses.length && !this.hasEveryStatuses(statuses)) return false

    return true
  }

  // Account Endpoints -----------------------

  login (model, showProgress) {
    return this._request({
      action: 'shared/account/login',
      method: 'POST',
      body: model,
      showProgress
    })
  }

  sendOtp (phoneNumber, showProgress) {
    return this._request({
      action: 'shared/account/send-otp',
      method: 'POST',
      params: { phoneNumber: String(parseInt(phoneNumber)) },
      showProgress
    })
  }

  otpLogin (model, showProgress) {
    return this._request({
      action: 'shared/account/otp-login',
      method: 'POST',
      body: model,
      showProgress
    })
  }

  sendResetPasswordEmail (model, showProgress) {
    return this._request({
      action: 'shared/account/send-reset-password-email',
      method: 'POST',
      body: model,
      showProgress
    })
  }

  sendResetPasswordSms (phoneNumber, showProgress) {
    return this._request({
      action: 'shared/account/send-reset-password-sms',
      method: 'POST',
      params: { phoneNumber: String(parseInt(phoneNumber)) },
      showProgress
    })
  }

  resetPasswordViaToken (model, showProgress) {
    return this._request({
      action: 'shared/account/reset-password-via-token',
      method: 'POST',
      body: model,
      showProgress
    })
  }

  resetPasswordViaCode (model, showProgress) {
    return this._request({
      action: 'shared/account/reset-password-via-code',
      method: 'POST',
      body: model,
      showProgress
    })
  }

  register (model, showProgress) {
    return this._request({
      action: 'shared/account/register',
      method: 'POST',
      body: model,
      showProgress
    })
  }

  logout (showProgress) {
    return this._request({
      action: 'shared/account/logout',
      method: 'POST',
      showProgress
    })
  }

  changePassword (model, showProgress) {
    return this._request({
      action: 'shared/account/change-password',
      method: 'POST',
      body: model,
      showProgress
    })
  }

  changeEmail (newEmail, showProgress) {
    return this._request({
      action: 'shared/account/change-email',
      method: 'POST',
      params: { newEmail },
      showProgress
    })
  }

  changePhoneNumber (newPhoneNumber, showProgress) {
    return this._request({
      action: 'shared/account/change-phone-number',
      method: 'POST',
      params: { newPhoneNumber: String(parseInt(newPhoneNumber)) },
      showProgress
    })
  }

  sendConfirmationEmail (model, showProgress) {
    return this._request({
      action: 'shared/account/send-confirmation-email',
      method: 'POST',
      body: model,
      showProgress
    })
  }

  confirmEmail (token, showProgress) {
    return this._request({
      action: 'shared/account/confirm-email',
      method: 'POST',
      params: { token },
      showProgress
    })
  }

  sendConfirmationSms (showProgress) {
    return this._request({
      action: 'shared/account/send-confirmation-sms',
      method: 'POST',
      showProgress
    })
  }

  confirmPhoneNumber (code, showProgress) {
    return this._request({
      action: 'shared/account/confirm-phone-number',
      method: 'POST',
      params: { code },
      showProgress
    })
  }
}
